import React, { useState } from "react";
import { SITE } from "../config/siteConfig";
import { Layout } from "../components/Layout";
import { PhoneButton } from "../components/PhoneButtons";
import useSeo from "../utils/seo";

// Pages institutionnelles et légales (liens du pied de page).
// Textes en français : utiliser des guillemets doubles ou du JSX, jamais d'apostrophe dans une chaîne entre apostrophes.

// À propos : qui édite le site, comment le service fonctionne.
export function AProposPage() {
  const c = SITE.company;
  const v = SITE.vertical;
  useSeo({
    title: "À propos",
    description: SITE.brandName + " est un service de comparaison d'offres " + v.product + " édité par " + c.name + ".",
    path: "/a-propos",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>À propos de {SITE.brandName}</h1>

        <h2>Qui sommes-nous ?</h2>
        <p>
          {SITE.brandName} ({SITE.siteLabel}) est un service de comparaison d'offres {v.product} destiné aux
          particuliers. Le site est édité par {c.name}, {c.form}, CIF {c.cif}, dont le siège est situé {c.address}.
          {" "}{c.name} est une agence de marketing digital spécialisée dans la mise en relation entre des particuliers
          et des professionnels de l'énergie et des télécoms.
        </p>

        <h2>Que faisons-nous ?</h2>
        <p>
          Nous aidons les particuliers à comparer les offres {v.product} proposées par une sélection de fournisseurs
          partenaires. La comparaison se fait par téléphone : les appels sont traités par les conseillers de{" "}
          {v.partner}, qui étudient votre situation et vous présentent les offres adaptées.
        </p>
        <p>
          {SITE.brandName} n'est{" "}
          {SITE.key === "energie"
            ? "ni un fournisseur d'énergie ni un gestionnaire de réseau"
            : "ni un fournisseur d'accès à internet ni un opérateur mobile"}
          . Nous ne représentons aucune des marques citées sur ce site et ne traitons pas les demandes relatives à un
          contrat existant (facture, résiliation, réclamation) : ces demandes doivent être adressées directement à
          votre fournisseur.
        </p>

        <h2>Comment sommes-nous rémunérés ?</h2>
        <p>
          Le service de comparaison est gratuit pour vous. L'appel est facturé au tarif d'un appel local, selon votre
          opérateur. Nous percevons une commission de notre partenaire lorsqu'un contrat est souscrit, sans incidence
          sur le prix que vous payez.
        </p>

        <h2>Nous contacter</h2>
        <ul className="plain-list">
          <li>Conseillers : {SITE.hours}</li>
          <li>
            E-mail : <a href={"mailto:" + c.email}>{c.email}</a>
          </li>
          <li>Adresse : {c.address}</li>
          <li>Directrice de la publication : l'administratrice de {c.name}</li>
        </ul>
        <div className="text-cta">
          <PhoneButton phone={SITE.phones.main} label="Comparer avec un conseiller" />
        </div>
      </article>
    </Layout>
  );
}

// Contact : téléphone, e-mail, adresse, formulaire (mailto).
export function ContactPage() {
  const c = SITE.company;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useSeo({
    title: "Contact",
    description: "Contacter " + SITE.brandName + " : téléphone, e-mail, adresse et horaires.",
    path: "/contact",
  });

  // Le formulaire ouvre la messagerie du visiteur : aucune donnée n'est envoyée ni enregistrée par le site.
  function onSubmit(e) {
    e.preventDefault();
    const subject = "Contact " + SITE.siteLabel + " - " + name;
    const body = "Nom : " + name + "\nE-mail : " + email + "\n\n" + message;
    window.location.href =
      "mailto:" + c.email + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);
  }

  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Contact</h1>

        <div className="contact-grid">
          <div>
            <h2>Comparer une offre par téléphone</h2>
            <p>Conseillers disponibles {SITE.hours}.</p>
            <PhoneButton phone={SITE.phones.main} label="Comparer avec un conseiller" />
            <p className="small">Service de comparaison gratuit · appel au tarif d'un appel local.</p>

            <h2>Éditeur du site</h2>
            <ul className="plain-list">
              <li>{c.name}</li>
              <li>CIF {c.cif}</li>
              <li>{c.address}</li>
              <li>Téléphone administratif : {c.phone}</li>
              <li>
                E-mail : <a href={"mailto:" + c.email}>{c.email}</a>
              </li>
              <li>Nous répondons aux e-mails {SITE.emailResponse}.</li>
            </ul>
            <p className="small">
              Pour une question sur un contrat existant (facture, résiliation, réclamation), contactez directement
              votre fournisseur : nous n'avons pas accès à votre contrat.
            </p>
          </div>

          <form className="contact-form" onSubmit={onSubmit}>
            <h2>Nous écrire</h2>
            <label>
              Nom
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
            </label>
            <label>
              E-mail
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label>
              Message
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} required />
            </label>
            <button type="submit" className="btn-submit">
              Envoyer
            </button>
            <p className="small">
              Le bouton ouvre votre messagerie avec votre message pré-rempli. Aucune donnée n'est enregistrée sur ce
              site.
            </p>
          </form>
        </div>
      </article>
    </Layout>
  );
}

// Questions fréquentes.
export function FAQPage() {
  const v = SITE.vertical;
  const isEnergy = SITE.key === "energie";
  useSeo({
    title: "Questions fréquentes",
    description: "Réponses aux questions fréquentes sur le service de comparaison " + SITE.brandName + ".",
    path: "/faq",
  });

  const items = [
    {
      q: "Qui êtes-vous ?",
      a:
        SITE.brandName +
        " est un service de comparaison d'offres " +
        v.product +
        " édité par " +
        SITE.company.name +
        ", société espagnole basée à Barcelone.",
    },
    {
      q: isEnergy ? "Êtes-vous un fournisseur d'énergie ?" : "Êtes-vous un opérateur ?",
      a: isEnergy
        ? "Non. Nous ne sommes ni un fournisseur d'énergie ni un gestionnaire de réseau, et nous n'agissons au nom d'aucune des marques citées sur ce site."
        : "Non. Nous ne sommes ni un fournisseur d'accès à internet ni un opérateur mobile, et nous n'agissons au nom d'aucune des marques citées sur ce site.",
    },
    {
      q: "Le service est-il gratuit ?",
      a:
        "Oui, la comparaison est gratuite. L'appel est facturé au tarif d'un appel local, selon votre opérateur. Nous sommes rémunérés par notre partenaire lorsqu'un contrat est souscrit, sans incidence sur le prix que vous payez.",
    },
    {
      q: "Qui me répond au téléphone ?",
      a: "Les conseillers de " + v.partner + ". Ils sont disponibles " + SITE.hours + ".",
    },
    {
      q: "Quelles offres comparez-vous ?",
      a: "Les offres d'une sélection de fournisseurs partenaires. La comparaison n'est pas exhaustive et ne couvre pas toutes les offres du marché.",
    },
    {
      q: "Puis-je joindre mon fournisseur actuel par votre numéro ?",
      a: "Non. Pour une question sur votre contrat actuel (facture, résiliation, réclamation), contactez directement votre fournisseur depuis son site officiel ou votre espace client.",
    },
    {
      q: "Suis-je obligé de souscrire ?",
      a: "Non. Vous êtes libre de ne rien souscrire après la comparaison.",
    },
  ];

  if (isEnergy) {
    items.push({
      q: "Que faire en cas de panne, de coupure ou d'odeur de gaz ?",
      a: "Nous ne traitons pas les urgences. " + SITE.emergency.map((e) => e.label + " : " + e.value + ".").join(" "),
    });
  }

  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Questions fréquentes</h1>
        {items.map((it) => (
          <section key={it.q} className="faq-item">
            <h2>{it.q}</h2>
            <p>{it.a}</p>
          </section>
        ))}
        <div className="text-cta">
          <PhoneButton phone={SITE.phones.main} label="Comparer avec un conseiller" />
        </div>
      </article>
    </Layout>
  );
}

// Avertissement : non-affiliation aux marques citées.
export function DisclaimerPage() {
  useSeo({
    title: "Avertissement",
    description: "Avertissement sur les informations publiées et les marques citées sur " + SITE.siteLabel + ".",
    path: "/avertissement",
  });
  const isEnergy = SITE.key === "energie";
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Avertissement</h1>
        <p>
          Les informations publiées sur {SITE.siteLabel} sont données à titre indicatif et ne constituent ni un conseil
          personnalisé ni une offre contractuelle. Les caractéristiques et les prix des offres sont communiqués par
          téléphone par les conseillers de {SITE.vertical.partner} et peuvent évoluer.
        </p>
        <p>
          {SITE.brandName} n'est{" "}
          {isEnergy
            ? "ni un fournisseur d'énergie ni un gestionnaire de réseau de distribution"
            : "ni un fournisseur d'accès à internet ni un opérateur mobile"}
          . {SITE.company.name} n'est affiliée à aucune des marques citées sur ce site et n'agit pas en leur nom. Les
          noms de marques et de fournisseurs sont cités uniquement pour permettre aux visiteurs d'identifier l'offre
          qu'ils souhaitent comparer ; ils restent la propriété de leurs titulaires respectifs.
        </p>
        <p>
          La comparaison porte sur une sélection de fournisseurs partenaires et n'est pas exhaustive. {SITE.company.name}{" "}
          est rémunérée par son partenaire lorsqu'un contrat est souscrit, sans incidence sur le prix payé par le
          client.
        </p>
        <p>
          Pour toute demande relative à un contrat existant (facture, résiliation, réclamation, dépannage), contactez
          directement votre fournisseur{isEnergy ? " ou le gestionnaire de réseau" : ""}.
        </p>
      </article>
    </Layout>
  );
}

// CGU et mentions légales.
export function CGUPage() {
  const c = SITE.company;
  const v = SITE.vertical;
  const isEnergy = SITE.key === "energie";
  useSeo({
    title: "Conditions générales d'utilisation et mentions légales",
    description: "Conditions générales d'utilisation et mentions légales du site " + SITE.siteLabel + ".",
    path: "/cgu",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Conditions générales d'utilisation et mentions légales</h1>
        <p className="small">Dernière mise à jour : octobre 2026</p>

        <h2>1. Éditeur du site</h2>
        <p>
          Le site {SITE.siteLabel} (le « Site ») est édité par {c.name}, {c.form}, {c.register}, CIF {c.cif}, dont le
          siège social est situé {c.address}.
        </p>
        <ul className="plain-list">
          <li>Téléphone administratif : {c.phone}</li>
          <li>
            E-mail : <a href={"mailto:" + c.email}>{c.email}</a>
          </li>
          <li>
            Protection des données : <a href={"mailto:" + c.privacyEmail}>{c.privacyEmail}</a>
          </li>
          <li>Directrice de la publication : l'administratrice de {c.name}</li>
        </ul>

        <h2>2. Définitions</h2>
        <ul>
          <li>« Service » : la comparaison gratuite d'offres {v.product} et la mise en relation téléphonique avec notre partenaire.</li>
          <li>« Utilisateur » : toute personne majeure et capable qui consulte le Site.</li>
          <li>
            « Partenaire » : papernest (Papernest Services SAS, RCS Paris 879 409 803), société tierce habilitée à commercialiser les offres comparées, vers laquelle les appels sont dirigés. Site :{" "}
            <a href="https://www.papernest.com" target="_blank" rel="noopener noreferrer">www.papernest.com</a>.
          </li>
        </ul>

        <h2>3. Objet</h2>
        <p>
          Les présentes conditions définissent les modalités d'accès et d'utilisation du Site. Elles ne régissent ni
          les services fournis par le Partenaire ou par les fournisseurs, ni les contrats conclus entre l'Utilisateur
          et un fournisseur.
        </p>

        <h2>4. Nature du service</h2>
        <p>
          Le Site présente un service de comparaison d'offres {v.product}. Les informations publiées sont données à
          titre indicatif et ne constituent pas un conseil personnalisé. {c.name} n'est{" "}
          {isEnergy
            ? "ni un fournisseur d'énergie ni un gestionnaire de réseau"
            : "ni un fournisseur d'accès à internet ni un opérateur mobile"}
          , ne commercialise aucune offre en son nom propre et ne traite aucun paiement.
        </p>

        <h2>5. Mise en relation téléphonique</h2>
        <p>
          Le Site affiche un numéro de téléphone qui dirige l'appel vers les conseillers du Partenaire, disponibles{" "}
          {SITE.hours}. Ce numéro n'est pas surtaxé : l'appel est facturé au tarif d'un appel local, selon l'opérateur
          de l'Utilisateur. {c.name} ne répond pas elle-même aux appels.
        </p>
        <p>
          La comparaison porte sur une sélection de fournisseurs partenaires et n'est pas exhaustive. {c.name} peut
          percevoir une commission du Partenaire lorsqu'un contrat est souscrit, sans incidence sur le prix payé par
          l'Utilisateur. Le Partenaire et les fournisseurs restent libres d'accepter ou de refuser une demande.
        </p>

        <h2>6. Marques citées</h2>
        <p>
          Les noms de fournisseurs cités sur le Site le sont uniquement pour permettre à l'Utilisateur d'identifier
          l'offre qu'il souhaite comparer. {c.name} n'est affiliée à aucune de ces marques et n'agit pas en leur nom.
          Pour toute demande relative à un contrat existant, l'Utilisateur doit contacter directement son fournisseur.
        </p>

        <h2>7. Accès au Site</h2>
        <p>
          Le Site est accessible en continu, sous réserve des interruptions nécessaires à sa maintenance ou en cas de
          force majeure. {c.name} peut modifier, suspendre ou interrompre l'accès au Site sans préavis.
        </p>

        <h2>8. Responsabilité</h2>
        <p>{c.name} ne peut être tenue responsable :</p>
        <ul>
          <li>de l'exactitude ou de l'exhaustivité des informations communiquées par le Partenaire ou les fournisseurs ;</li>
          <li>de l'indisponibilité temporaire ou définitive de certaines offres ;</li>
          <li>des conséquences d'un choix fondé sur des informations incomplètes.</li>
        </ul>
        <p>L'Utilisateur reste seul responsable du choix de son fournisseur.</p>

        <h2>9. Engagements de l'Utilisateur</h2>
        <p>
          L'Utilisateur s'engage à utiliser le Site conformément à la loi, à ne pas en perturber le fonctionnement et à
          fournir des informations exactes au Partenaire lors de son appel.
        </p>

        <h2>10. Propriété intellectuelle</h2>
        <p>
          Les éléments propres au Site (textes, mise en page, logo {SITE.brandName}) sont protégés. Toute reproduction
          sans autorisation de {c.name} est interdite. Les marques de tiers restent la propriété de leurs titulaires.
        </p>

        <h2>11. Données personnelles et cookies</h2>
        <p>
          Le traitement des données personnelles et l'utilisation des cookies sont décrits dans la{" "}
          <a href="/confidentialite">politique de confidentialité</a> et la <a href="/cookies">politique de cookies</a>.
        </p>

        <h2>12. Modification des conditions</h2>
        <p>
          Les présentes conditions peuvent être modifiées à tout moment. La version en ligne lors de la consultation du
          Site s'applique.
        </p>

        <h2>13. Droit applicable</h2>
        <p>
          Les présentes conditions sont régies par le droit français. En cas de litige, les tribunaux compétents sont
          ceux du ressort de la cour d'appel de Paris, sauf disposition impérative contraire.
        </p>

        <h2>14. Contact</h2>
        <p>
          Pour toute question sur le Site ou les présentes conditions : <a href={"mailto:" + c.email}>{c.email}</a>
        </p>
      </article>
    </Layout>
  );
}

// Politique de confidentialité (RGPD).
export function ConfidentialitePage() {
  const c = SITE.company;
  useSeo({
    title: "Politique de confidentialité",
    description: "Données traitées, finalités, durées de conservation et droits des utilisateurs du site " + SITE.siteLabel + ".",
    path: "/confidentialite",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Politique de confidentialité</h1>
        <p className="small">Dernière mise à jour : octobre 2026</p>

        <h2>1. Responsable du traitement</h2>
        <p>
          {c.name}, CIF {c.cif}, {c.address}. Contact protection des données :{" "}
          <a href={"mailto:" + c.privacyEmail}>{c.privacyEmail}</a>
        </p>

        <h2>2. Données traitées</h2>
        <p>Le Site ne comporte aucun formulaire qui enregistre vos données. Nous traitons uniquement :</p>
        <ul>
          <li>
            des données techniques de navigation (adresse IP, type de navigateur et d'appareil, pages consultées, page
            de provenance) ;
          </li>
          <li>
            des données de suivi des appels : un identifiant de session aléatoire, l'identifiant de mesure d'audience
            Google (_ga) s'il existe, la page consultée et la page de provenance, associés à l'appel passé depuis le
            Site (numéro de suivi appelé, date, heure et durée de l'appel) ;
          </li>
          <li>
            le contenu des e-mails que vous nous envoyez volontairement (le formulaire de contact ouvre simplement votre
            messagerie).
          </li>
        </ul>
        <p>Nous ne traitons aucune donnée sensible au sens de l'article 9 du RGPD.</p>

        <h2>3. Finalités et bases légales</h2>
        <ul>
          <li>Fonctionnement et sécurité du Site : intérêt légitime.</li>
          <li>
            Rattachement d'un appel à la campagne publicitaire d'origine et facturation entre partenaires : intérêt
            légitime.
          </li>
          <li>Mesure d'audience et publicité Google : votre consentement, que vous pouvez retirer à tout moment.</li>
          <li>Réponse à vos demandes par e-mail : exécution de mesures précontractuelles ou intérêt légitime.</li>
        </ul>

        <h2>4. Destinataires</h2>
        <p>
          Les données sont destinées à {c.name} et à ses sous-traitants techniques : l'hébergeur du Site, le prestataire
          de suivi d'appels et, si vous l'acceptez, Google (mesure d'audience et publicité). Les appels sont traités par{" "}
          {SITE.vertical.partner}, qui agit comme responsable de ses propres traitements pour les données que vous lui
          communiquez au téléphone. Certains prestataires peuvent être situés hors de l'Union européenne ; les
          transferts sont alors encadrés par les clauses contractuelles types de la Commission européenne ou une
          décision d'adéquation.
        </p>

        <h2>5. Durées de conservation</h2>
        <ul>
          <li>Données de suivi des appels : 13 mois au maximum.</li>
          <li>Cookies : voir la <a href="/cookies">politique de cookies</a>.</li>
          <li>E-mails : le temps nécessaire au traitement de la demande, puis 3 ans au maximum.</li>
        </ul>

        <h2>6. Vos droits</h2>
        <p>
          Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de
          portabilité, ainsi que du droit de retirer votre consentement à tout moment. Pour les exercer, écrivez à{" "}
          <a href={"mailto:" + c.privacyEmail}>{c.privacyEmail}</a> ou à l'adresse postale ci-dessus. Une preuve
          d'identité peut vous être demandée en cas de doute raisonnable.
        </p>
        <p>
          Vous pouvez introduire une réclamation auprès de l'Agencia Española de Protección de Datos (aepd.es) ou de
          l'autorité de votre pays de résidence (en France : la CNIL, cnil.fr).
        </p>

        <h2>7. Sécurité</h2>
        <p>
          Le Site est servi en HTTPS. Nous mettons en œuvre des mesures techniques et organisationnelles adaptées pour
          protéger les données et notifierons toute violation présentant un risque élevé dans les délais légaux.
        </p>

        <h2>8. Modification</h2>
        <p>Cette politique peut être mise à jour. La date de dernière mise à jour figure en haut de la page.</p>
      </article>
    </Layout>
  );
}

// Politique de cookies (dont cookies du suivi d'appels).
export function CookiesPage() {
  useSeo({
    title: "Politique de cookies",
    description: "Cookies et traceurs utilisés sur " + SITE.siteLabel + " et gestion de votre consentement.",
    path: "/cookies",
  });

  function reset() {
    try {
      localStorage.removeItem("consentMode");
    } catch {
      // stockage indisponible
    }
    window.location.reload();
  }

  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Politique de cookies</h1>
        <p className="small">Dernière mise à jour : octobre 2026</p>
        <p>
          Les cookies et traceurs sont de petits fichiers ou informations enregistrés sur votre appareil. Seuls les
          traceurs nécessaires au fonctionnement du Site et au suivi des appels sont utilisés sans consentement. Les
          traceurs de mesure d'audience et de publicité Google ne sont activés que si vous les acceptez.
        </p>

        <h2>Traceurs utilisés</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Finalité</th>
                <th>Durée</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>consentMode (stockage local)</td>
                <td>Nécessaire</td>
                <td>Mémoriser votre choix concernant les cookies.</td>
                <td>Jusqu'à suppression</td>
              </tr>
              <tr>
                <td>call_session_id</td>
                <td>Suivi des appels</td>
                <td>Identifiant de session aléatoire permettant de rattacher un appel à la visite et à la campagne d'origine.</td>
                <td>15 minutes</td>
              </tr>
              <tr>
                <td>odi_[numéro]</td>
                <td>Suivi des appels</td>
                <td>Mémorise le numéro de suivi attribué pendant la visite, pour afficher le même numéro sur toutes les pages.</td>
                <td>15 minutes</td>
              </tr>
              <tr>
                <td>_ga, _gcl_au et cookies Google</td>
                <td>Mesure d'audience / publicité</td>
                <td>Mesurer l'audience et l'efficacité des annonces Google. Déposés uniquement si vous acceptez.</td>
                <td>Jusqu'à 13 mois</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Suivi des appels</h2>
        <p>
          Lorsque vous consultez le Site, les numéros de téléphone affichés peuvent être remplacés par un numéro de
          suivi. L'identifiant de session, l'identifiant Google (_ga) s'il existe, la page consultée et la page de
          provenance sont transmis à notre prestataire de suivi d'appels pour rattacher l'appel à la campagne
          publicitaire d'origine. Ces informations ne servent pas à vous identifier nominativement.
        </p>

        <h2>Gérer votre choix</h2>
        <p>
          Vous pouvez modifier votre choix à tout moment :{" "}
          <button type="button" className="link-button" onClick={reset}>
            afficher à nouveau le bandeau cookies
          </button>
          . Vous pouvez aussi bloquer ou supprimer les cookies depuis les réglages de votre navigateur. Pour en savoir
          plus sur l'utilisation des données par Google :{" "}
          <a href="https://business.safety.google/privacy/" target="_blank" rel="noopener noreferrer">
            business.safety.google/privacy
          </a>
          .
        </p>
      </article>
    </Layout>
  );
}
